import { reactive, computed, watch, toRefs, resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, createVNode, normalizeStyle, toDisplayString } from 'vue';
import propTypes from './utils/prop-types';
import { INPUT_EVENT, CHANGE_EVENT, INCREASE_EVENT, DECREASE_EVENT } from './utils/constants';
import { createComponent } from './utils/create-component';
import { addPrecisely } from './utils/base';
import { addPxUnit } from './utils/format';
import Icon from './icon';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * 步进器尺寸
   */
  size: propTypes.string().def('normal'),
  /**
   * 默认值
   */
  defaultValue: propTypes.number().def(0),
  /**
   * 绑定值
   */
  modelValue: propTypes.number().def(0),
  /**
   * 是否允许键盘输入
   */
  readonly: propTypes["boolean"]().def(false),
  /**
   * 是否只允许输入整数
   */
  integer: propTypes["boolean"]().def(false),
  /**
   * 允许输入的最小值
   */
  min: propTypes.number().def(Number.MIN_SAFE_INTEGER),
  /**
   * 允许输入的最大值
   */
  max: propTypes.number().def(Number.MAX_SAFE_INTEGER),
  /**
   * 点击左右按钮时的输入间隔
   */
  step: propTypes.number().def(1),
  /**
   * 是否禁用
   */
  disabled: propTypes["boolean"]().def(false),
  /**
   * 数字区域的宽度，默认36px
   */
  width: propTypes.oneOfType([propTypes.number(), propTypes.string()]).def(36),
  /**
   * 数字区域是否随输入自动撑开
   */
  autoWidth: propTypes["boolean"]().def(false)
};
var emits = [INPUT_EVENT, CHANGE_EVENT, INCREASE_EVENT, DECREASE_EVENT];

var _createComponent = createComponent('stepper'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  components: _defineProperty({}, Icon.name, Icon),
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var emit = _ref.emit;
    var editing = false;
    var state = reactive({
      currentValue: props.modelValue || props.defaultValue
    });
    // 减少按钮样式
    var reduceButtonClassNames = computed(function () {
      // 达到最小限制
      var reachMin = state.currentValue <= props.min;
      return ["".concat(className, "__btn"), "".concat(className, "__btn_reduce"), _defineProperty({}, "".concat(className, "__btn-disabled"), reachMin || props.disabled)];
    });
    // 增加按钮样式
    var addButtonClassNames = computed(function () {
      // 达到最大限制
      var reachMax = state.currentValue >= props.max;
      return ["".concat(className, "__btn"), "".concat(className, "__btn_add"), _defineProperty({}, "".concat(className, "__btn-disabled"), reachMax || props.disabled)];
    });
    var inputStyle = computed(function () {
      var width = props.width,
        autoWidth = props.autoWidth;
      var widthStr = addPxUnit(width);
      if (autoWidth) {
        return {
          minWidth: widthStr
        };
      }
      return {
        width: widthStr
      };
    });
    // 获取在min-max，以及integer限制内的数字
    function getLimitedValue(val) {
      if (props.integer) {
        val = Math.round(val);
      }
      return Math.min(props.max, Math.max(props.min, val));
    }
    function changeCurrentValueWidthStep(step) {
      if (props.disabled) {
        return;
      }
      state.currentValue = getLimitedValue(addPrecisely(state.currentValue, step));
    }
    function onAdd() {
      changeCurrentValueWidthStep(props.step);
    }
    function onReduce() {
      changeCurrentValueWidthStep(-props.step);
    }
    function onFocus() {
      editing = true;
    }
    function onInput(e) {
      state.currentValue = e.target.value;
    }
    function onBlur() {
      editing = false;
      state.currentValue = getLimitedValue(state.currentValue);
    }
    watch(function () {
      return state.currentValue;
    }, function (val, oldVal) {
      // 编辑中不触发change事件
      if (!editing && val !== props.modelValue) {
        emit(INPUT_EVENT, val);
        emit(CHANGE_EVENT, val);
      }
      var diff = val - oldVal;
      if (!diff) {
        return;
      }
      if (diff > 0) {
        emit(INCREASE_EVENT, diff);
      } else if (diff < 0) {
        emit(DECREASE_EVENT, diff);
      }
    });
    watch(function () {
      return props.modelValue;
    }, function (val) {
      if (val !== undefined) {
        state.currentValue = getLimitedValue(val);
      }
    });
    return _objectSpread2(_objectSpread2({}, toRefs(state)), {}, {
      className: className,
      inputStyle: inputStyle,
      reduceButtonClassNames: reduceButtonClassNames,
      addButtonClassNames: addButtonClassNames,
      onAdd: onAdd,
      onReduce: onReduce,
      onInput: onInput,
      onFocus: onFocus,
      onBlur: onBlur
    });
  }
});

var _hoisted_1 = ["step", "min", "max", "readonly", "value", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_icon = resolveComponent("sp-icon");
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass([_ctx.className, "".concat(_ctx.className, "-").concat(_ctx.size)])
  }, [createElementVNode("div", {
    "class": normalizeClass(_ctx.reduceButtonClassNames),
    onClick: _cache[0] || (_cache[0] = function () {
      return _ctx.onReduce && _ctx.onReduce.apply(_ctx, arguments);
    })
  }, [createVNode(_component_sp_icon, {
    "class": normalizeClass("".concat(_ctx.className, "__icon")),
    name: "base_subtract_24"
  }, null, 8 /* PROPS */, ["class"])], 2 /* CLASS */), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__input")),
    style: normalizeStyle(_ctx.inputStyle)
  }, [createElementVNode("span", null, toDisplayString(_ctx.currentValue), 1 /* TEXT */), createElementVNode("input", {
    type: "number",
    step: _ctx.step,
    min: _ctx.min,
    max: _ctx.max,
    readonly: _ctx.readonly,
    value: _ctx.currentValue,
    onInput: _cache[1] || (_cache[1] = function () {
      return _ctx.onInput && _ctx.onInput.apply(_ctx, arguments);
    }),
    onFocus: _cache[2] || (_cache[2] = function () {
      return _ctx.onFocus && _ctx.onFocus.apply(_ctx, arguments);
    }),
    onBlur: _cache[3] || (_cache[3] = function () {
      return _ctx.onBlur && _ctx.onBlur.apply(_ctx, arguments);
    }),
    disabled: _ctx.disabled
  }, null, 40 /* PROPS, HYDRATE_EVENTS */, _hoisted_1)], 6 /* CLASS, STYLE */), createElementVNode("div", {
    "class": normalizeClass(_ctx.addButtonClassNames),
    onClick: _cache[4] || (_cache[4] = function () {
      return _ctx.onAdd && _ctx.onAdd.apply(_ctx, arguments);
    })
  }, [createVNode(_component_sp_icon, {
    "class": normalizeClass("".concat(_ctx.className, "__icon")),
    name: "base_add_24"
  }, null, 8 /* PROPS */, ["class"])], 2 /* CLASS */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "components/src/component/stepper/stepper.vue";

var _Stepper = script;

export { _Stepper as default };
